import { Routes, Route, Link, NavLink, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import ScheduleHome from './components/ScheduleHome';
import CreateSchedule from './components/CreateSchedule';
import CreateNewSchedule from '../calendar/CreateNewSchedule';
import SchedulesConfiguration from './components/SchedulesConfiguration';
import ManageSchedules from './components/ManageSchedules';
import ScheduleInvitation from './components/ScheduleInvitation';
import ScheduleApps from './components/ScheduleApps';
import CalendarIcon from '../../../assets/icons/CalendarIcon';
import CreateCalendarIcon from '../../../assets/icons/CreateCalendarIcon';
import ScheduleConfigIcon from '../../../assets/icons/ScheduleConfigIcon';
import MeetingManagerIcon from '../../../assets/icons/MeetingManagerIcon';
import AppsIcon from '../../../assets/icons/AppsIcon';
import { useLazyQuery } from '@apollo/client';
import { LIST_CALENDAR_RULER } from '~/services/schedule';
import { GET_INTEGRATIONS_BY_TYPE } from '~/services/store';

const Schedule = () => {
  const [t] = useTranslation('schedule');
  const location = useLocation();
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error', id: number} | null>(null);
  const [showScheduleInvitation, setShowScheduleInvitation] = useState(true);

  const [getCalendarRulers, { loading, error, data: calendarsData }] =
    useLazyQuery(LIST_CALENDAR_RULER, {
      variables: { data: { agent_id: agentId } },
      fetchPolicy: 'network-only',
    });

  const [getCalendarIntegrations, { loading: integrationsLoading, error: integrationsError, data: integrationsData }] =
    useLazyQuery(GET_INTEGRATIONS_BY_TYPE, {
      variables: { data: { agent_id: agentId, type_integration: 'SCHEDULER' } },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        // Check if we have any SCHEDULER integrations
        const integrationData = data?.get_integrations_by_type?.data;
        const hasCalendarIntegration =
          integrationData &&
          typeof integrationData === 'object' &&
          Object.keys(integrationData).length > 0;

        if (hasCalendarIntegration) {
          setShowScheduleInvitation(false);
        }
      },
    });

  useEffect(() => {
    if (agentId) {
      getCalendarRulers();
      getCalendarIntegrations();
    }
  }, [agentId, getCalendarRulers, getCalendarIntegrations]);

  // Helper function to check if calendar integration exists
  const hasCalendarIntegration = () => {
    const integrationData = integrationsData?.get_integrations_by_type?.data;
    return integrationData &&
      typeof integrationData === 'object' &&
      Object.keys(integrationData).length > 0;
  };
  
  // Detectar parámetros de acción en la URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    
    if (action === 'created' || action === 'updated') {
      const toastId = Date.now();
      setToast({
        message: t(action === 'created' ? 'create_success' : 'update_success'),
        type: 'success',
        id: toastId
      });
      
      // Hide invitation when schedule is created/updated
      setShowScheduleInvitation(false);
      
      // Limpiar el parámetro de la URL
      window.history.replaceState({}, document.title, location.pathname);
      
      // Auto-ocultar después de 5 segundos
      const timer = setTimeout(() => {
        setToast(prevToast => prevToast && prevToast.id === toastId ? null : prevToast);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [location.search, t]);

  const handleCreateSchedule = () => {
    // Hide the invitation screen when creating a schedule
    setShowScheduleInvitation(false);
    
    if (showScheduleInvitation) {
      navigate('create?first=true');
    } else {
      navigate('create');
    }
  };

  return (
    <div className="mx-auto h-fit w-full max-w-[1200px] px-0 pt-20 md:px-8">
      {/* Toast notification */}
      {toast && (
        <div className="toast toast-end toast-top z-50 mr-4 mt-4">
          <div className={`alert ${toast.type === 'success' ? 'alert-success text-success-content' : 'alert-error text-error-content'} shadow-lg animate-fadeInRight`}>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                {toast.type === 'success' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 stroke-current" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 stroke-current" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span className="font-medium">{toast.message}</span>
              </div>
              <button 
                onClick={() => setToast(null)} 
                className="ml-4 hover:opacity-75 transition-opacity"
                aria-label="Cerrar notificación"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 stroke-current" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      <h1 className="text-2xl font-bold">{t('title')}</h1>
      <h4 className="mb-5">{t('schedule_description')}</h4>

      {integrationsLoading ? (
        /* Show loading skeleton while checking integrations */
        <div className="mb-24 h-[70vh] w-full overflow-y-scroll rounded-2xl bg-base-100 p-6 lg:mb-0">
          <div className="flex min-h-[60vh] items-center justify-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="loading loading-spinner loading-lg text-primary"></div>
              <p className="text-base-content/70 text-lg">{t('loading', 'Cargando...')}</p>
            </div>
          </div>
        </div>
      ) : showScheduleInvitation ? (
        /* Show only the invitation message covering the full content area */
        <div className="mb-24 h-[70vh] w-full overflow-y-scroll rounded-2xl bg-base-100 p-6 lg:mb-0">
          <div className="flex min-h-[60vh] items-center justify-center">
            <ScheduleInvitation onCreateSchedule={handleCreateSchedule} />
          </div>
        </div>
      ) : (
        /* Show the normal tabs and content when invitation is hidden */
        <>
          <div className="flex border-b border-gray-200 mb-6">
            <NavLink 
              to="home" 
              className={({ isActive }) => {
                const hasIntegration = hasCalendarIntegration();
                return `flex items-center py-3 px-6 mr-4 ${
                  !hasIntegration 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : isActive 
                      ? 'border-b-2 border-primary text-primary font-medium' 
                      : 'text-gray-600 hover:text-gray-800'
                }`;
              }}
              onClick={(e) => {
                const hasIntegration = hasCalendarIntegration();
                if (!hasIntegration) {
                  e.preventDefault();
                }
              }}
            >
              {({ isActive }) => {
                const hasIntegration = hasCalendarIntegration();
                return (
                  <>
                    <CalendarIcon isActive={isActive && hasIntegration} />
                    <span>{t('all_schedules')}</span>
                  </>
                );
              }}
            </NavLink>
            <NavLink 
              to="create" 
              className={({ isActive }) => 
                `flex items-center py-3 px-6 mr-4 ${
                  isActive 
                    ? 'border-b-2 border-primary text-primary font-medium' 
                    : 'text-gray-600 hover:text-gray-800'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <CreateCalendarIcon isActive={isActive} />
                  <span>{t('create_schedule')}</span>
                </>
              )}
            </NavLink>
            <NavLink 
              to="manage_schedules" 
              className={({ isActive }) => 
                `flex items-center py-3 px-6 mr-4 ${
                  isActive 
                    ? 'border-b-2 border-primary text-primary font-medium' 
                    : 'text-gray-600 hover:text-gray-800'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <MeetingManagerIcon isActive={isActive} />
                  <span>{t('manage_links') || 'Enlaces'}</span>
                </>
              )}
            </NavLink>
            <NavLink 
              to="configuration" 
              className={({ isActive }) => {
                const hasIntegration = hasCalendarIntegration();
                return `flex items-center py-3 px-6 mr-4 ${
                  !hasIntegration 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : isActive 
                      ? 'border-b-2 border-primary text-primary font-medium' 
                      : 'text-gray-600 hover:text-gray-800'
                }`;
              }}
              onClick={(e) => {
                const hasIntegration = hasCalendarIntegration();
                if (!hasIntegration) {
                  e.preventDefault();
                }
              }}
            >
              {({ isActive }) => {
                const hasIntegration = hasCalendarIntegration();
                return (
                  <>
                    <ScheduleConfigIcon isActive={isActive && hasIntegration} />
                    <span>{t('manage_schedules')}</span>
                  </>
                );
              }}
            </NavLink>
            <NavLink 
              to="apps" 
              className={({ isActive }) => 
                `flex items-center py-3 px-6 mr-4 ${
                  isActive 
                    ? 'border-b-2 border-primary text-primary font-medium' 
                    : 'text-gray-600 hover:text-gray-800'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <AppsIcon isActive={isActive} />
                  <span>{t('apps')}</span>
                </>
              )}
            </NavLink>
          </div>

          <div className="mb-24 h-[70vh] w-full overflow-y-scroll rounded-2xl bg-base-100 p-6 lg:mb-0">
            <Routes>
              <Route path="/" element={<Navigate to="home" replace />} />
              <Route path="home" element={<ScheduleHome />} />
              <Route path="create" element={<CreateSchedule />} />
              <Route path="edit/:scheduleId" element={<CreateSchedule />} />
              <Route path="configuration" element={<SchedulesConfiguration />} />
              <Route path="manage_schedules" element={<ManageSchedules />} />
              <Route path="apps" element={<ScheduleApps />} />
            </Routes>
          </div>
        </>
      )}
    </div>
  );
};

export default Schedule;
